import { useState, useEffect, useCallback, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Mail, Phone, ChevronDown, Code, Lightbulb, Users, Sparkles, Heart, ArrowRight, Volume2, VolumeX, Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5087fc03-107b-4401-834f-abbff4041565/artifacts/qsq1zb0d_adigidi.JPG";
const ORIGINAL_ART_URL = "https://customer-assets.emergentagent.com/job_yin-yang-dev/artifacts/9s88qy7p_adigidi_orginal.JPG";
const VOICE_URL = "https://customer-assets.emergentagent.com/job_yin-yang-dev/artifacts/a2twsnp6_adigidi_voice.mp3";

// Protected Image Component
const ProtectedImage = ({ src, alt, className, ...props }) => {
  const handleContextMenu = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    return false;
  }, []);

  return (
    <div className="image-container" onContextMenu={handleContextMenu}>
      <img
        src={src}
        alt={alt}
        className={`protected-image ${className || ''}`}
        onContextMenu={handleContextMenu}
        onDragStart={handleDragStart}
        draggable={false}
        {...props}
      />
    </div>
  );
};

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export const LandingPage = () => {
  const [activeSection, setActiveSection] = useState("hero");
  const [isPlaying, setIsPlaying] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const audioRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);

  // Audio player function
  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  // Handle audio end
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleEnded = () => setIsPlaying(false);
      audio.addEventListener('ended', handleEnded);
      return () => audio.removeEventListener('ended', handleEnded);
    }
  }, []);

  // Prevent right-click on the entire page
  useEffect(() => {
    const handleContextMenu = (e) => {
      if (e.target.tagName === 'IMG' || e.target.closest('.image-container')) {
        e.preventDefault();
        return false;
      }
    };

    const handleKeyDown = (e) => {
      // Prevent Ctrl+S, Ctrl+U, Ctrl+Shift+I
      if ((e.ctrlKey && (e.key === 's' || e.key === 'u')) || 
          (e.ctrlKey && e.shiftKey && e.key === 'I')) {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["hero", "story", "services", "philosophy", "contact"];
      const scrollPosition = window.scrollY + window.innerHeight / 3;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative" data-testid="landing-page">
      {/* Hidden Audio Element */}
      <audio ref={audioRef} src={VOICE_URL} preload="auto" />

      {/* Floating Navigation - Desktop */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="fixed top-6 left-1/2 -translate-x-1/2 z-50 nav-pill px-4 md:px-6 py-3 rounded-full hidden md:block"
        data-testid="main-navigation"
      >
        <ul className="flex items-center gap-4 lg:gap-6 text-sm font-medium">
          {[
            { id: "hero", label: "Home" },
            { id: "story", label: "Story" },
            { id: "services", label: "Services" },
            { id: "philosophy", label: "Philosophy" },
            { id: "contact", label: "Contact" }
          ].map((item) => (
            <li key={item.id}>
              <button
                onClick={() => scrollToSection(item.id)}
                className={`transition-all duration-300 scribble-underline ${
                  activeSection === item.id
                    ? "text-[#00B0F0]"
                    : "text-gray-300 hover:text-white"
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            </li>
          ))}
        </ul>
      </motion.nav>

      {/* Mobile Navigation */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="fixed top-4 right-4 z-50 md:hidden"
      >
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="nav-pill p-3 rounded-full"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <Menu className="w-6 h-6 text-white" />
          )}
        </button>
      </motion.div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-lg md:hidden flex items-center justify-center"
        >
          <ul className="flex flex-col items-center gap-8 text-xl font-medium">
            {[
              { id: "hero", label: "Home" },
              { id: "story", label: "Story" },
              { id: "services", label: "Services" },
              { id: "philosophy", label: "Philosophy" },
              { id: "contact", label: "Contact" }
            ].map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => {
                    scrollToSection(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`transition-all duration-300 ${
                    activeSection === item.id
                      ? "text-[#00B0F0]"
                      : "text-gray-300 hover:text-white"
                  }`}
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </motion.div>
      )}

      {/* Hero Section - Split Screen */}
      <section id="hero" className="min-h-screen flex flex-col lg:flex-row" data-testid="hero-section">
        {/* Curiosity Side - Light */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="w-full lg:w-1/2 min-h-[50vh] lg:min-h-screen bg-white flex flex-col items-center justify-center p-8 lg:p-16 relative"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            <motion.div
              className="mx-auto mb-8"
              whileHover={{ rotate: 5, scale: 1.05 }}
            >
              <ProtectedImage
                src={LOGO_URL}
                alt="adigidi logo - colorful abstract art"
                className="w-48 h-48 lg:w-64 lg:h-64 object-contain yin-yang-circle logo-container"
                data-testid="logo-image"
              />
            </motion.div>
            <h2 className="font-handwritten text-4xl lg:text-5xl text-[#111827] mb-4">
              curiosity
            </h2>
            <p className="font-body text-[#9CA3AF] text-lg max-w-sm">
              Born from a child's imagination
            </p>
          </motion.div>

          {/* Decorative scribble elements */}
          <svg className="absolute bottom-10 left-10 w-24 h-24 opacity-30" viewBox="0 0 100 100">
            <motion.path
              d="M10,50 Q30,20 50,50 T90,50"
              stroke="#E52E4E"
              strokeWidth="3"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Ambition Side - Dark */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="w-full lg:w-1/2 min-h-[50vh] lg:min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-8 lg:p-16 relative"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="text-center"
          >
            <h1 className="font-heading text-5xl lg:text-7xl font-black text-white mb-6 tracking-tight">
              adigidi
            </h1>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-[#F3F4F6] mb-4">
              ambition
            </h2>
            <p className="font-body text-[#9CA3AF] text-lg max-w-sm mb-8">
              Tailored software solutions for unique businesses
            </p>
            <Button
              onClick={() => scrollToSection("contact")}
              className="bg-white text-[#0A0A0A] hover:bg-[#00B0F0] hover:text-white rounded-full px-8 py-3 font-medium transition-all duration-300"
              data-testid="cta-button"
            >
              Let's Build Together
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>

          {/* Decorative code lines */}
          <svg className="absolute top-10 right-10 w-32 h-32 opacity-20" viewBox="0 0 100 100">
            <motion.path
              d="M20,20 L40,20 M50,20 L80,20 M20,40 L60,40 M70,40 L90,40 M20,60 L30,60 M40,60 L80,60"
              stroke="#00B0F0"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
            />
          </svg>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </motion.div>
      </section>

      {/* Story Section */}
      <section
        id="story"
        className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-gray-100 py-24 lg:py-32"
        data-testid="story-section"
      >
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-heading text-4xl lg:text-6xl font-black text-[#111827] mb-16 text-center"
            >
              The <span className="gradient-text">Origin</span> Story
            </motion.h2>

            {/* Story Cards */}
            <div className="space-y-16">
              {/* The Name */}
              <motion.div variants={fadeInUp} className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-full bg-[#E52E4E]/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-10 h-10 text-[#E52E4E]" />
                </div>
                <div>
                  <h3 className="font-handwritten text-3xl text-[#E52E4E] mb-3">The Name</h3>
                  <p className="font-body text-[#111827] text-lg leading-relaxed">
                    When my daughter was just 2 years old, she started calling me{" "}
                    <span className="inline-flex items-center gap-2">
                      <span className="font-handwritten text-2xl text-[#00B0F0]">"adigidi"</span>
                      <motion.button
                        onClick={playAudio}
                        className={`inline-flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 ${
                          isPlaying 
                            ? "bg-[#00B0F0] text-white" 
                            : "bg-[#00B0F0]/20 text-[#00B0F0] hover:bg-[#00B0F0]/40"
                        }`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        data-testid="play-voice-btn"
                        title="Hear how it's pronounced"
                      >
                        {isPlaying ? (
                          <VolumeX className="w-4 h-4" />
                        ) : (
                          <Volume2 className="w-4 h-4" />
                        )}
                      </motion.button>
                    </span>
                    {" "}— a name born 
                    from pure imagination. No one in our family, no friend had ever used it. 
                    It was hers alone to create, and somehow, it felt like it represented the 
                    yin and yang of ancient Chinese philosophy — balance, duality, and harmony.
                  </p>
                </div>
              </motion.div>

              {/* The Art */}
              <motion.div variants={fadeInUp} className="flex flex-col items-center gap-8">
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-[#00B0F0]/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-10 h-10 text-[#00B0F0]" />
                  </div>
                  <h3 className="font-handwritten text-3xl text-[#00B0F0]">The Art</h3>
                </div>
                <p className="font-body text-[#111827] text-lg leading-relaxed text-center max-w-2xl">
                  A few months ago, my now 5-year-old daughter discovered color pens on my desk. 
                  What followed was pure, unfiltered creativity — abstract strokes of vibrant colors 
                  dancing across the page. That scribble became our logo, as abstract and beautiful 
                  as the name she gave me.
                </p>
                
                {/* Before/After Art Showcase */}
                <motion.div 
                  className="w-full max-w-4xl mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">
                    {/* Original Scribbling */}
                    <motion.div 
                      className="flex flex-col items-center"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <div className="absolute -inset-3 bg-gradient-to-r from-[#E52E4E]/20 via-[#00B0F0]/20 to-[#5CB85C]/20 rounded-2xl blur-xl" />
                        <ProtectedImage
                          src={ORIGINAL_ART_URL}
                          alt="Original scribbling on book"
                          className="relative w-64 h-64 lg:w-72 lg:h-72 object-cover rounded-xl shadow-2xl border-4 border-white"
                          data-testid="original-art"
                        />
                      </div>
                      <p className="font-handwritten text-xl text-[#9CA3AF] mt-4">The Original</p>
                      <p className="font-body text-sm text-[#9CA3AF]">Pure creativity on paper</p>
                    </motion.div>

                    {/* Arrow/Transform indicator */}
                    <motion.div 
                      className="flex flex-col items-center gap-2"
                      animate={{ x: [0, 10, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <div className="hidden lg:flex items-center gap-2">
                        <Sparkles className="w-6 h-6 text-[#FFEE00]" />
                        <ArrowRight className="w-8 h-8 text-[#00B0F0]" />
                        <Sparkles className="w-6 h-6 text-[#FFEE00]" />
                      </div>
                      <div className="lg:hidden flex items-center gap-2 rotate-90">
                        <Sparkles className="w-5 h-5 text-[#FFEE00]" />
                        <ArrowRight className="w-6 h-6 text-[#00B0F0]" />
                        <Sparkles className="w-5 h-5 text-[#FFEE00]" />
                      </div>
                      <p className="font-handwritten text-sm text-[#00B0F0] hidden lg:block">AI Magic</p>
                    </motion.div>

                    {/* Extracted Logo */}
                    <motion.div 
                      className="flex flex-col items-center"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="relative">
                        <div className="absolute -inset-3 bg-gradient-to-r from-[#5CB85C]/20 via-[#FFEE00]/20 to-[#E52E4E]/20 rounded-full blur-xl" />
                        <ProtectedImage
                          src={LOGO_URL}
                          alt="AI extracted logo"
                          className="relative w-64 h-64 lg:w-72 lg:h-72 object-contain rounded-full shadow-2xl bg-white p-4"
                          data-testid="extracted-logo"
                        />
                      </div>
                      <p className="font-handwritten text-xl text-[#9CA3AF] mt-4">The Logo</p>
                      <p className="font-body text-sm text-[#9CA3AF]">Extracted & refined</p>
                    </motion.div>
                  </div>
                </motion.div>
              </motion.div>

              {/* The Support */}
              <motion.div variants={fadeInUp} className="flex flex-col lg:flex-row items-center gap-8">
                <div className="w-20 h-20 rounded-full bg-[#5CB85C]/10 flex items-center justify-center flex-shrink-0">
                  <Heart className="w-10 h-10 text-[#5CB85C]" />
                </div>
                <div>
                  <h3 className="font-handwritten text-3xl text-[#5CB85C] mb-3">The Support</h3>
                  <p className="font-body text-[#111827] text-lg leading-relaxed">
                    Behind every ambitious dream stands unwavering support. My wife has been the 
                    silent strength, nurturing both our daughter's creativity and my entrepreneurial 
                    spirit. Together, we're not just building a company — we're building a legacy 
                    inspired by family.
                  </p>
                </div>
              </motion.div>

              {/* The Vision */}
              <motion.div variants={fadeInUp} className="flex flex-col lg:flex-row-reverse items-center gap-8">
                <div className="w-20 h-20 rounded-full bg-[#FFEE00]/20 flex items-center justify-center flex-shrink-0">
                  <Code className="w-10 h-10 text-[#111827]" />
                </div>
                <div>
                  <h3 className="font-handwritten text-3xl text-[#111827] mb-3">The Vision</h3>
                  <p className="font-body text-[#111827] text-lg leading-relaxed">
                    The curiosity of a child asking "why?" meets the ambition of a father 
                    answering "why not?" — that's{" "}
                    <span className="font-handwritten text-2xl gradient-text">adigidi</span>. 
                    A software development company born from imagination, driven by purpose, 
                    and committed to crafting unique solutions for every unique business.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeInUp} className="mt-20 text-center">
              <p className="font-handwritten text-2xl text-[#9CA3AF]">
                — Vijay Reddy, Founder
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Services Section - Dark */}
      <section
        id="services"
        className="min-h-screen bg-[#0A0A0A] py-24 lg:py-32 flex items-center"
        data-testid="services-section"
      >
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="max-w-5xl mx-auto text-center"
          >
            {/* Animated listening icon */}
            <motion.div
              variants={fadeInUp}
              className="mb-12 flex justify-center"
            >
              <motion.div
                className="relative w-32 h-32"
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                {/* Animated rings */}
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-[#00B0F0]/30"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                />
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-[#E52E4E]/30"
                  animate={{ scale: [1, 1.8, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.3 }}
                />
                <motion.div
                  className="absolute inset-0 rounded-full border-2 border-[#5CB85C]/30"
                  animate={{ scale: [1, 2.1, 1], opacity: [0.5, 0, 0.5] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
                />
                {/* Center icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#E52E4E] via-[#00B0F0] to-[#5CB85C] p-[2px]">
                    <div className="w-full h-full rounded-full bg-[#0A0A0A] flex items-center justify-center">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.h2
              variants={fadeInUp}
              className="font-heading text-4xl lg:text-6xl font-black text-white mb-8"
            >
              We're{" "}
              <span className="relative inline-block">
                <motion.span
                  className="gradient-text"
                  animate={{ opacity: [1, 0.7, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Listening
                </motion.span>
                <motion.svg
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 200 10"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                >
                  <motion.path
                    d="M0,5 Q50,0 100,5 T200,5"
                    stroke="url(#gradient)"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#E52E4E" />
                      <stop offset="50%" stopColor="#00B0F0" />
                      <stop offset="100%" stopColor="#5CB85C" />
                    </linearGradient>
                  </defs>
                </motion.svg>
              </span>
            </motion.h2>

            <motion.p
              variants={fadeInUp}
              className="font-body text-[#9CA3AF] text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto mb-8"
            >
              Every small and medium business has a{" "}
              <span className="text-white font-medium">unique challenge</span>.
              <br className="hidden lg:block" />
              We don't offer cookie-cutter solutions.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="space-y-6 mb-12"
            >
              <p className="font-handwritten text-3xl lg:text-4xl text-white">
                Tell us your{" "}
                <motion.span
                  className="inline-block text-[#E52E4E]"
                  animate={{ rotate: [-2, 2, -2] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                >
                  problem
                </motion.span>
                ,
              </p>
              <p className="font-handwritten text-3xl lg:text-4xl text-white">
                and we'll craft{" "}
                <motion.span
                  className="inline-block text-[#00B0F0]"
                  animate={{ y: [-2, 2, -2] }}
                  transition={{ duration: 0.6, repeat: Infinity }}
                >
                  your
                </motion.span>{" "}
                <motion.span
                  className="inline-block text-[#5CB85C]"
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 0.7, repeat: Infinity }}
                >
                  solution
                </motion.span>
                .
              </p>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <Button
                onClick={() => scrollToSection("contact")}
                className="bg-white text-[#0A0A0A] hover:bg-[#00B0F0] hover:text-white rounded-full px-10 py-4 font-medium text-lg transition-all duration-300 group"
                data-testid="services-cta"
              >
                Share Your Challenge
                <motion.span
                  className="inline-block ml-2"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.span>
              </Button>
            </motion.div>

            {/* Floating keywords */}
            <motion.div
              variants={fadeInUp}
              className="mt-16 flex flex-wrap justify-center gap-4"
            >
              {["Automation", "Integration", "Custom Apps", "Data Solutions", "Cloud", "APIs"].map((word, i) => (
                <motion.span
                  key={word}
                  className="px-4 py-2 rounded-full border border-white/10 text-[#9CA3AF] text-sm font-body"
                  animate={{ 
                    y: [0, -5, 0],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{ 
                    duration: 2 + i * 0.2, 
                    repeat: Infinity,
                    delay: i * 0.1
                  }}
                  whileHover={{ 
                    borderColor: "#00B0F0",
                    color: "#ffffff",
                    scale: 1.05
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section
        id="philosophy"
        className="min-h-screen bg-gradient-to-b from-gray-100 to-white py-24 lg:py-32 flex items-center"
        data-testid="philosophy-section"
      >
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="max-w-5xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-heading text-4xl lg:text-6xl font-black text-[#111827] mb-16 text-center"
            >
              The Philosophy of <span className="font-handwritten gradient-text">Balance</span>
            </motion.h2>

            <div className="flex flex-col lg:flex-row items-center gap-16">
              {/* Yin Yang Visual */}
              <motion.div
                variants={fadeInUp}
                className="w-full lg:w-1/2 flex justify-center"
              >
                <motion.div
                  className="relative w-64 h-64 lg:w-80 lg:h-80"
                  animate={{ rotate: 360 }}
                  transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
                >
                  <ProtectedImage
                    src={LOGO_URL}
                    alt="adigidi philosophy"
                    className="w-full h-full object-contain rounded-full shadow-2xl"
                  />
                </motion.div>
              </motion.div>

              {/* Philosophy Text */}
              <motion.div variants={fadeInUp} className="w-full lg:w-1/2 space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-white border-2 border-[#0A0A0A] mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-xl font-bold text-[#111827] mb-2">
                      ādi — The Light
                    </h3>
                    <p className="font-body text-[#9CA3AF]">
                      Creativity, curiosity, and the freedom to imagine. Like a child's 
                      uninhibited scribbles, we approach problems with fresh eyes and 
                      unlimited possibility.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-3 h-3 rounded-full bg-[#0A0A0A] mt-2 flex-shrink-0" />
                  <div>
                    <h3 className="font-heading text-xl font-bold text-[#111827] mb-2">
                      giḍī — The Dark
                    </h3>
                    <p className="font-body text-[#9CA3AF]">
                      Precision, expertise, and the discipline to execute. The professional 
                      foundation that transforms creative visions into robust, reliable software.
                    </p>
                  </div>
                </div>

                <Separator className="my-8" />

                <p className="font-body text-lg text-[#111827] leading-relaxed">
                  At <span className="font-handwritten text-2xl gradient-text">adigidi</span>, 
                  we believe the best software emerges from this balance — the{" "}
                  <span className="font-handwritten text-xl text-[#E52E4E]">curiosity</span> to 
                  question everything and the{" "}
                  <span className="font-heading font-bold text-[#00B0F0]">ambition</span> to 
                  build solutions that matter.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section - Dark */}
      <section
        id="contact"
        className="min-h-[60vh] bg-[#0A0A0A] py-24 lg:py-32 flex items-center"
        data-testid="contact-section"
      >
        <div className="container mx-auto px-6 lg:px-16">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h2
              variants={fadeInUp}
              className="font-heading text-4xl lg:text-6xl font-black text-white mb-6"
            >
              Let's Create Together
            </motion.h2>
            <motion.p
              variants={fadeInUp}
              className="font-body text-[#9CA3AF] text-lg mb-12"
            >
              Ready to transform your vision into reality? Let's talk.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-8 mb-12"
            >
              <a
                href="mailto:mahika@adigidi.com"
                className="flex items-center gap-3 text-white hover:text-[#00B0F0] transition-colors group"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#00B0F0]/20 transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <span className="font-body text-lg">mahika@adigidi.com</span>
              </a>

              <a
                href="tel:+919886166990"
                className="flex items-center gap-3 text-white hover:text-[#E52E4E] transition-colors group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#E52E4E]/20 transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <span className="font-body text-lg">+91 9886166990</span>
              </a>
            </motion.div>

            <motion.div variants={fadeInUp}>
              <Separator className="bg-white/10 my-12" />
              <p className="font-handwritten text-2xl text-[#9CA3AF] mb-2">
                Vijay Reddy
              </p>
              <p className="font-body text-[#9CA3AF] text-sm">
                Founder, adigidi
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/10 py-8">
        <div className="container mx-auto px-6 lg:px-16">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ProtectedImage
                src={LOGO_URL}
                alt="adigidi"
                className="w-8 h-8 rounded-full object-contain"
              />
              <span className="font-handwritten text-xl text-white">adigidi</span>
            </div>
            <p className="font-body text-[#9CA3AF] text-sm">
              © 2025 adigidi. Crafted with curiosity and ambition.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
